import type { Router } from 'vue-router';
import { usePermissionStore } from '@/stores/permission';

/**
 * 路由权限守卫
 *
 * 在 router.beforeEach 中检查路由 meta.permission 配置，
 * 若用户无所需权限则重定向到 /403 页面。
 *
 * 使用方式：
 * ```ts
 * import { setupPermissionGuard } from './permission-guard';
 * setupPermissionGuard(router);
 * ```
 *
 * 路由配置示例：
 * ```ts
 * { path: '/roles', meta: { permission: 'role:view' } }
 * ```
 *
 * @see requirements.md - Requirement 12.5
 */
export function setupPermissionGuard(router: Router): void {
  router.beforeEach((to) => {
    const requiredPermission = to.meta.permission as string | string[] | undefined;

    // 路由未配置权限要求则放行
    if (!requiredPermission) return true;

    const permissionStore = usePermissionStore();

    if (to.meta.superOnly && !permissionStore.isSuper) {
      return { path: '/403' };
    }

    // 权限数据尚未加载时放行（由 auth 守卫负责拉取）
    if (permissionStore.permissions.length === 0 && !permissionStore.isSuper) {
      return true;
    }

    // 检查权限
    const hasAccess = Array.isArray(requiredPermission)
      ? permissionStore.hasAnyPermission(requiredPermission)
      : permissionStore.hasPermission(requiredPermission);

    if (!hasAccess) {
      return { path: '/403' };
    }

    return true;
  });
}
